import { Box, Checkbox, Divider, makeStyles, TextField, Typography } from '@material-ui/core'
import { Modal } from 'components/core/modal'
import { FunctionComponent } from 'react'
import { RadioButtonChecked, RadioButtonUnchecked } from '@material-ui/icons'
import styled from 'styled-components/macro'
import { UploadFeedbackFile } from './UploadFeedbackFile'
import { useFeedback } from 'pages/auth/hooks/useFeedback'
import { Button } from 'common/atoms/Button/Button'
import { InputFieldGroup } from 'common/molecules/InputFieldGroup/InputFieldGroup'
import { CustomDivider } from 'common/atoms/Divider/CustomDivider'

const StyledDescriptionBox = styled(Box)`
  && {
    border: 1px solid ${props => props.theme.colors.accent1};
    border-radius: 15px;
  }
`
const StyledBox = styled(Box)`
  margin-bottom: 0.5em;
  color: ${props => props.theme.colors.accent1};
`
const StyledTextField = styled(TextField)`
  .MuiInput-input {
    scrollbar-width: none;
    -ms-overflow-style: none;
    &::-webkit-scrollbar {
      display: none;
    }
    overflow-y: scroll;
  }
  .MuiInput-root {
    border: none;
  }
  .MuiInput-underline {
    &:before,
    &:after,
    &:hover {
      border-bottom: 0.15px solid ${props => props.theme.colors.supplementary};
    }
  }
`
const StyledDisabledTextField = styled(TextField)`
  .MuiInputBase-input {
    color: ${props => props.theme.colors.accent};
  }
`
interface FeedbackModalProps {
  show: boolean
  onClose: Function
  onAdd: Function
}

export const FeedbackModal: FunctionComponent<FeedbackModalProps> = ({ show, onClose, onAdd }) => {
  const {
    theme,
    notify,
    backendUser,
    getFeedbackCount,
    setFeedbackCount,
    maxFeedbackLength,
    getProcessedFile,
    checked,
    setChecked,
    isValidated,
    setValidation,
    currentFeedback,
    setCurrentFeedback,
    fileName,
    setFileName
  } = useFeedback()

  const handleChange = (e, type: string) => {
    switch (type) {
      case 'complaint':
        setCurrentFeedback({
          ...currentFeedback,
          userId: backendUser?.id,
          emailAddress: backendUser?.username,
          category: type
        })
        setChecked(prev => ({ ...prev, 0: !!prev }))
        break
      case 'compliment':
        setCurrentFeedback({
          ...currentFeedback,
          userId: backendUser?.id,
          emailAddress: backendUser?.username,
          category: type
        })
        setChecked(prev => ({ ...prev, 1: !!prev }))
        break
      case 'content':
        setCurrentFeedback({ ...currentFeedback, [e?.target?.name]: e?.target?.value })
        setFeedbackCount(e?.target?.value?.length)
        setValidation(e?.target?.value)
        break
    }
  }

  const handleSubmit = () => {
    onClose(true)
    setCurrentFeedback(undefined)
    setChecked([false, false])
    setFeedbackCount(0)
    onAdd(currentFeedback)
    notify('success', 'Thank you for your valuable feedback')
    setFileName('')
  }
  const useStyles = makeStyles(() => ({
    helperText: {
      textAlign: 'end',
      color: `${theme.colors.accent1}`
    }
  }))
  const classes = useStyles()
  return (
    <Modal showCancel show={show} onClose={onClose} noBorder title="" noMargin>
      <Box display="flex" justifyContent="center" margin={'0 1.5rem'}>
        <Box width="100em">
          <Box justifyContent="center" display="flex">
            <Typography variant="h5">
              <Box fontWeight="bold" color={theme.colors.primary}>
                Feedback Form
              </Box>
            </Typography>
          </Box>
          <Box justifyContent="center" display="flex" mb="2em">
            <Typography component="span" variant="body1">
              <Box fontWeight="bold" color={theme.colors.accent1}>
                We would like your feedback to improve our platform
              </Box>
            </Typography>
          </Box>
        </Box>
      </Box>
      <CustomDivider color={theme.colors.supplementary1} borderType="solid" thickness="1px" />
      <Box display="grid" gridRowGap="1em" mt="3em" margin={'0 1.5rem 1.5rem'}>
        <Box>
          <Typography variant="body1">
            <StyledBox>Name</StyledBox>
          </Typography>
          <InputFieldGroup
            value={backendUser?.firstName?.concat(' ').concat(backendUser?.lastName)}
            name="name"
            fullWidth
            disabled={true}
          />
        </Box>
        <Box>
          <Typography variant="body1">
            <StyledBox>Email</StyledBox>
          </Typography>
          <InputFieldGroup value={backendUser?.username} name="email" fullWidth disabled={true} />
        </Box>
        <Box mt="1em" color={theme.colors.accent1}>
          <Typography>
            <Box>
              Please select your feedback category below
              <Box color={theme.colors.error} component="span">
                <sup>*</sup>
              </Box>
            </Box>
          </Typography>
          <Box mt=".50em" display="flex" flexDirection="column">
            <Box display="flex" justifyContent="left" alignItems="center">
              <Checkbox
                size="small"
                style={{ padding: '0 .25em 0 0', color: theme.colors.accent1 }}
                icon={<RadioButtonUnchecked />}
                checkedIcon={<RadioButtonChecked />}
                onChange={e => {
                  handleChange(e, e?.target?.name)
                }}
                onClick={() => setChecked(prev => ({ ...prev, 1: false }))}
                name="complaint"
                checked={checked[0]}
              />
              <Box display="inline" mr="2em">
                <Typography component="span" variant="body1">
                  Something is not quite right
                </Typography>
              </Box>
            </Box>
            <Box display="flex" justifyContent="left" alignItems="center">
              <Checkbox
                size="small"
                style={{ padding: '0 .25em 0 0', color: theme.colors.accent1 }}
                icon={<RadioButtonUnchecked />}
                checkedIcon={<RadioButtonChecked />}
                onChange={e => {
                  handleChange(e, e?.target?.name)
                }}
                onClick={() => setChecked(prev => ({ ...prev, 0: false }))}
                name="compliment"
                checked={checked[1]}
              />
              <Box display="inline">
                <Typography component="span" variant="body1">
                  Compliment
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
        <Box>
          <Typography variant="body1">
            <StyledBox>
              Feedback
              <Box color={theme.colors.error} component="span">
                <sup>*</sup>
              </Box>
            </StyledBox>
          </Typography>

          <InputFieldGroup
            error={getFeedbackCount == maxFeedbackLength}
            charLimit={1200}
            showCounter
            placeholder="Type Message"
            multiline
            fullWidth
            InputProps={{
              rows: 3,
              disableUnderline: true
            }}
            name="content"
            value={currentFeedback?.content || ''}
            inputProps={{ maxLength: maxFeedbackLength, disableUnderline: true }}
            onChange={e => {
              handleChange(e, e?.target?.name)
            }}
            FormHelperTextProps={{
              className: classes.helperText
            }}
          />
        </Box>
        <Box>
          <Typography variant="body1">
            <Box color={theme.colors.accent1}>Attach a file</Box>
          </Typography>
          <UploadFeedbackFile
            theme={theme}
            getProcessedFile={getProcessedFile}
            feedback={currentFeedback}
            setFeedback={setCurrentFeedback}
            fileName={fileName}
            setFileName={setFileName}
          />
        </Box>
        <Button
          design="primary"
          fullWidth
          onClick={() => handleSubmit()}
          disabled={!isValidated || (!checked[0] && !checked[1])}
        >
          <Typography component="span" variant="body2">
            <Box fontWeight="bold">SUBMIT</Box>
          </Typography>
        </Button>
      </Box>
    </Modal>
  )
}
